import { useEffect, type ReactElement } from "react";
import type { KpiWatchlistParams } from "@playbookai/widget-schemas";
import { Checkbox } from "../../atoms/Checkbox/Checkbox.js";
import { EmptyState } from "../../molecules/EmptyState/index.js";
import { useKpiCatalog } from "../kpi-catalog.js";
import { CatalogPickerControls } from "../CatalogPickerControls.js";
import type { DashboardWidgetEditorProps } from "../types.js";
import { MAX_WATCHLIST_KPIS } from "./constants.js";

const LEGEND =
  "flex items-center justify-between text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold mb-1.5";

/**
 * `kpiWatchlist` editor — pick up to six KPIs from the host-provided catalogue.
 * Data-blind: `useKpiCatalog()` supplies the choices; `null` → empty-state.
 */
export function KpiWatchlistEditor({
  draftParams,
  onChange,
}: DashboardWidgetEditorProps): ReactElement {
  const catalog = useKpiCatalog();
  const params = draftParams as KpiWatchlistParams;
  const selected = new Set(params.kpiIds ?? []);
  const activateCatalog = catalog?.activate;
  useEffect(() => {
    activateCatalog?.(params.kpiIds ?? []);
  }, [activateCatalog, params.kpiIds]);

  const toggle = (id: string): void => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      if (next.size >= MAX_WATCHLIST_KPIS) return;
      next.add(id);
    }
    onChange({ ...params, kpiIds: [...next] });
  };

  if (!catalog) {
    return <EmptyState size="sm" title="KPI catalogue unavailable." />;
  }

  return (
    <fieldset>
      <legend className={LEGEND}>
        <span>KPIs in watchlist</span>
        <span className="pb-tabular normal-case tracking-normal">
          {selected.size} / {MAX_WATCHLIST_KPIS}
        </span>
      </legend>
      <div className="max-h-64 overflow-auto rounded-md border border-pb-border divide-y divide-pb-border/60">
        {catalog.kpis.map((k) => {
          const checked = selected.has(k.id);
          return (
            <Checkbox
              key={k.id}
              checked={checked}
              disabled={!checked && selected.size >= MAX_WATCHLIST_KPIS}
              onChange={() => toggle(k.id)}
              className="w-full px-2 py-1.5"
              label={
                <span className="flex-1 flex items-center justify-between gap-2">
                  <span className="text-sm text-pb-text truncate">
                    {k.name}
                  </span>
                  {catalog.archivedKpiIds?.includes(k.id) ? (
                    <span className="text-dense-2 text-pb-text-muted">
                      Archived
                    </span>
                  ) : null}
                  <span className="text-dense-1 text-pb-text-muted pb-tabular">
                    {k.value}
                  </span>
                </span>
              }
            />
          );
        })}
      </div>
      <div className="mt-2">
        <CatalogPickerControls
          label="KPIs"
          query={catalog.searchQuery}
          isLoading={catalog.isLoading}
          isLoadingMore={catalog.isLoadingMore}
          error={catalog.error}
          hasMore={catalog.hasMore}
          isEmpty={
            !catalog.isLoading && !catalog.error && catalog.kpis.length === 0
          }
          onSearchChange={catalog.onSearchChange}
          onLoadMore={catalog.onLoadMore}
          onRetry={catalog.onRetry}
        />
      </div>
      <p className="mt-1.5 text-dense-1 text-pb-text-muted">
        Up to {MAX_WATCHLIST_KPIS} KPIs.
      </p>
    </fieldset>
  );
}
